import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get, update, set } from "firebase/database";
import { country } from "../constants/country";
import { toastrError } from "../components/toastr";
import { v4 } from "uuid";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 outline-none text-sm text-gray-900 placeholder-gray-400 focus:border-primary";

// ─── Step 1 ───────────────────────────────────────────────────────────────────
function Step1({ data, onChange, onNext }) {
  const isValid = data.companyName.trim() && data.country;
  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">
          Restaurant / Business Name
        </label>
        <input
          value={data.companyName}
          name="companyName"
          className={inputClass}
          placeholder="e.g. The Golden Fork"
          onChange={onChange}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">
          Country
        </label>
        <select
          value={data.country}
          name="country"
          className={inputClass}
          onChange={onChange}
        >
          <option value="">Select your country</option>
          {country.map((c) => (
            <option key={c.code} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={onNext}
        disabled={!isValid}
        className={`w-full py-3 rounded-xl font-semibold text-sm mt-2 ${
          isValid
            ? "bg-primary text-white"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
        }`}
      >
        Continue
      </button>
    </div>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────
function Step2({ tables, setTables, onNext, onSkip }) {
  const [tableNum, setTableNum] = useState("");
  const [tableLabel, setTableLabel] = useState("");
  const [tableCapacity, setTableCapacity] = useState("");

  function addTable() {
    if (!tableNum.trim()) return;
    setTables([
      ...tables,
      {
        id: v4(),
        number: tableNum.trim(),
        label: tableLabel.trim(),
        capacity: tableCapacity ? parseInt(tableCapacity, 10) : null,
        status: "free",
        currentCustomerId: null,
      },
    ]);
    setTableNum("");
    setTableLabel("");
    setTableCapacity("");
  }

  function removeTable(id) {
    setTables(tables.filter((t) => t.id !== id));
  }

  return (
    <div className="flex flex-col gap-y-4">
      <p className="text-sm text-gray-500">
        Add your tables so staff can assign customers when they check in. You
        can always add or change these later in Advanced Settings.
      </p>

      <div className="flex flex-col gap-y-2">
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Table Number *</label>
            <input
              value={tableNum}
              onChange={(e) => setTableNum(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="e.g. 1"
            />
          </div>
          <div className="w-20">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Seats</label>
            <input
              value={tableCapacity}
              onChange={(e) => setTableCapacity(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="4"
              type="number"
              min="1"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">
            Label <span className="text-gray-400">(optional — e.g. Window, Patio, VIP)</span>
          </label>
          <input
            value={tableLabel}
            onChange={(e) => setTableLabel(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
            placeholder="e.g. Window seat or Couple or VIP seat"
          />
        </div>
      </div>
      <button
        onClick={addTable}
        disabled={!tableNum.trim()}
        className={`w-full py-2.5 rounded-xl text-sm font-semibold ${
          tableNum.trim()
            ? "bg-primary text-white"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
        }`}
      >
        + Add Table
      </button>

      {tables.length > 0 && (
        <div className="flex flex-col gap-y-2">
          {tables.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3"
            >
              <div>
                <span className="text-sm font-semibold text-gray-800">
                  Table {t.number}
                </span>
                {t.label && (
                  <span className="text-xs text-gray-500 ml-2">{t.label}</span>
                )}
                {t.capacity && (
                  <span className="text-xs text-gray-400 ml-2">
                    · {t.capacity} seats
                  </span>
                )}
              </div>
              <button
                onClick={() => removeTable(t.id)}
                className="text-red-400 text-xs font-medium"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 mt-2">
        <button
          onClick={onSkip}
          className="flex-1 py-3 rounded-xl font-semibold text-sm border border-gray-200 text-gray-600"
        >
          Skip for now
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 rounded-xl font-semibold text-sm bg-primary text-white"
        >
          {tables.length > 0 ? "Save & Continue" : "Continue"}
        </button>
      </div>
    </div>
  );
}

// ─── Step 3 ───────────────────────────────────────────────────────────────────
function Step3({ onFinish, saving }) {
  const tips = [
    {
      emoji: "➕",
      title: "Add customers",
      desc: "Tap Add in the bottom nav to add a customer to the queue.",
    },
    {
      emoji: "✅",
      title: "Check them in",
      desc: "When their table is ready, tap Check In to seat them.",
    },
    {
      emoji: "📱",
      title: "Share your queue",
      desc: "Go to Settings to get your public link — customers can track their wait.",
    },
  ];

  return (
    <div className="flex flex-col gap-y-3">
      {tips.map((tip) => (
        <div
          key={tip.title}
          className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4"
        >
          <span className="text-2xl leading-none mt-0.5">{tip.emoji}</span>
          <div>
            <p className="text-sm font-semibold text-gray-800">{tip.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{tip.desc}</p>
          </div>
        </div>
      ))}
      <button
        onClick={onFinish}
        disabled={saving}
        className="w-full py-3 rounded-xl font-semibold text-sm bg-primary text-white mt-3 disabled:opacity-60"
      >
        {saving ? "Setting up…" : "Go to Dashboard"}
      </button>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
function Onboarding() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ companyName: "", country: "" });
  const [tables, setTables] = useState([]);

  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  async function finish() {
    setSaving(true);
    try {
      const database = getDatabase();
      const userInfoRef = ref(database, "userInfo");
      const snapshot = await get(userInfoRef);
      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (k) => allData[k].email === email
        );
        if (userKey) {
          const ownerId = allData[userKey].id;
          await update(ref(database, `userInfo/${userKey}`), {
            companyName: formData.companyName,
            country: formData.country,
            onboardingComplete: true,
          });
          if (tables.length > 0 && ownerId) {
            for (const table of tables) {
              await set(ref(database, `tables/${ownerId}/${table.id}`), table);
            }
          }
          if (ownerId) localStorage.setItem("ownerId", ownerId);
        }
      }
      localStorage.setItem("onboardingComplete", "true");
      navigate("/dashboard");
    } catch (err) {
      toastrError("Could not complete setup. Please try again.");
      setSaving(false);
    }
  }

  const stepTitles = [
    "About your restaurant",
    "Set up your tables",
    "You're all set!",
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-start px-5 py-10">
      {/* Header */}
      <div className="text-center mb-8 w-full max-w-sm">
        <img
          src="logo.png"
          alt="WaitLessQ"
          className="w-14 h-14 rounded-2xl mx-auto mb-3"
        />
        <h1 className="text-xl font-bold text-gray-900">WaitLessQ</h1>
        <p className="text-sm text-gray-500 mt-1">Let's set up your restaurant</p>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                s <= step ? "bg-primary" : "bg-gray-200"
              }`}
            />
            {i < 2 && (
              <div
                className={`w-8 h-0.5 ${s < step ? "bg-primary" : "bg-gray-200"}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <h2 className="text-base font-bold text-gray-900 mb-0.5">
          {stepTitles[step - 1]}
        </h2>
        <p className="text-xs text-gray-400 mb-5">Step {step} of 3</p>

        {step === 1 && (
          <Step1 data={formData} onChange={handleChange} onNext={() => setStep(2)} />
        )}
        {step === 2 && (
          <Step2
            tables={tables}
            setTables={setTables}
            onNext={() => setStep(3)}
            onSkip={() => setStep(3)}
          />
        )}
        {step === 3 && <Step3 onFinish={finish} saving={saving} />}
      </div>
    </div>
  );
}

export default Onboarding;
